import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, Tag, Ticket } from 'lucide-react';
import { useReveal } from '../hooks/useReveal';
import { getEvents } from '../lib/api';
import { priceSummary, fmtTimeRange, fmtLongDate, parseDay, isPast } from '../lib/events';
import { IMAGES } from '../mock';
import RsvpForm from './RsvpForm';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function EventTile({ e, onRsvp, delay }) {
  const d = parseDay(e.date);
  return (
    <article data-testid={`home-event-${e.id}`} style={{ transitionDelay: `${delay}ms` }}
      className="group bg-white rounded-sm overflow-hidden shadow-[0_6px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_14px_44px_rgba(0,0,0,0.12)] transition-shadow duration-500 flex flex-col">
      <Link to={`/events/${e.id}`} className="relative h-[200px] overflow-hidden block">
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[900ms] group-hover:scale-105" style={{ backgroundImage: `url(${e.image || IMAGES.heroA})` }} />
        {d && (
          <div className="absolute top-4 left-4 bg-white rounded-sm px-3 py-2 text-center shadow">
            <p className="font-sans-ah text-[11px] tracking-wide-ah text-[#4a9cd4]">{MON[d.getMonth()]}</p>
            <p className="font-serif-ah text-[28px] leading-none text-[#2f2f2f]">{d.getDate()}</p>
          </div>
        )}
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <Link to={`/events/${e.id}`}><h3 className="font-serif-ah text-[24px] text-[#2f2f2f] leading-snug mb-3 hover:text-[#4a9cd4] transition-colors">{e.title}</h3></Link>
        <div className="space-y-1 font-sans-ah text-[13px] text-[#777] mb-5">
          {fmtTimeRange(e) && <p className="flex items-center gap-1.5"><Clock size={14} /> {fmtTimeRange(e)}</p>}
          {e.location && <p className="flex items-center gap-1.5"><MapPin size={14} /> {e.location}</p>}
          <p className="flex items-center gap-1.5 text-[#4a9cd4]" data-testid={`event-price-${e.id}`}><Tag size={14} /> {priceSummary(e)}</p>
        </div>
        <button onClick={() => onRsvp(e)} data-testid={`home-quick-rsvp-${e.id}`}
          className="btn-ah-solid mt-auto self-start font-sans-ah text-[12px] px-5 py-3 rounded-sm flex items-center gap-2">
          <Ticket size={15} /> QUICK RSVP
        </button>
      </div>
    </article>
  );
}

export default function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => { getEvents().then((d) => setEvents(d.filter((e) => !isPast(e.date)).slice(0, 3))).catch(() => {}); }, []);
  if (!events.length) return null;
  return <UpcomingEventsSection events={events} />;
}

// Rendered only once events exist so useReveal's ref is attached on mount
function UpcomingEventsSection({ events }) {
  const [ref, inView] = useReveal();
  const [active, setActive] = useState(null);

  return (
    <section className="bg-[#f8f6f2] py-24 md:py-28" data-testid="home-upcoming-events">
      <div ref={ref} className={`max-w-[1440px] mx-auto px-6 lg:px-10 reveal ${inView ? 'in-view' : ''}`}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-3">UPCOMING EVENTS</p>
            <h2 className="font-serif-ah text-[36px] md:text-[46px] text-[#2f2f2f] leading-none">Save your spot</h2>
          </div>
          <Link to="/events" data-testid="home-all-events-link" className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4] group">
            ALL EVENTS <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className={`grid gap-8 ${events.length === 1 ? 'md:grid-cols-1 max-w-[460px]' : events.length === 2 ? 'md:grid-cols-2 max-w-[960px]' : 'md:grid-cols-3'}`}>
          {events.map((e, i) => <EventTile key={e.id} e={e} onRsvp={setActive} delay={i * 120} />)}
        </div>
      </div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-[600px] max-h-[90vh] overflow-y-auto rounded-sm" data-testid="quick-rsvp-dialog">
          <DialogTitle className="font-serif-ah text-[26px] text-[#2f2f2f] font-normal pr-6">{active?.title}</DialogTitle>
          <DialogDescription className="font-sans-ah text-[13px] text-[#777]">
            {active && [fmtLongDate(active.date), fmtTimeRange(active), active.location].filter(Boolean).join(' • ')}
          </DialogDescription>
          {active && <RsvpForm key={active.id} eventId={active.id} eventTitle={active.title} event={active} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}
